// src/pages/MeterManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, FileText, Users } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import './MeterManagementPage.css';

interface Song {
  song_id: number;
  title: string;
  composer_name: string | null;
  lyricist_name: string | null;
  tune_id: number | null;
  tune_name: string | null;
}

interface MeterGroup {
  meter_id: number;
  meter: string;
  description: string | null;
  abbreviation: string | null;
  song_count: number;
  songs: Song[];
}

interface MeterData {
  success: boolean;
  data: MeterGroup[];
  summary: {
    total_meters: number;
    total_songs: number;
  };
}

interface MeterManagementPageProps {
  hideNavigation?: boolean;
}

const MeterManagementPage: React.FC<MeterManagementPageProps> = ({ hideNavigation = false }) => {
  const [meterData, setMeterData] = useState<MeterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedMeters, setExpandedMeters] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadMeterData();
  }, []);

  const loadMeterData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/meters/songs');
      setMeterData(response.data);
    } catch (err) {
      console.error('Error loading meter data:', err);
      setError('Failed to load meter data');
    } finally {
      setLoading(false);
    }
  };

  const toggleMeterExpansion = (meterId: number) => {
    setExpandedMeters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(meterId)) {
        newSet.delete(meterId);
      } else {
        newSet.add(meterId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/settings" className="btn btn-secondary">
          <ArrowLeft size={16} className="me-2" />
          Back to Settings
        </Link>
      </div>
    );
  }

  return (
    <>
      {!hideNavigation && <Navigation />}
      <div className="container mt-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <Link to="/settings" className="btn btn-outline-secondary me-3">
          <ArrowLeft size={16} />
        </Link>
        <Music className="me-2" size={32} />
        <h1>Meter Management</h1>
      </div>

      {/* Summary */}
      {meterData && (
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card text-center">
              <div className="card-body">
                <Music size={24} className="text-primary mb-2" />
                <h5>{meterData.summary.total_meters}</h5>
                <small className="text-muted">Total Meters</small>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card text-center">
              <div className="card-body">
                <FileText size={24} className="text-info mb-2" />
                <h5>{meterData.summary.total_songs}</h5>
                <small className="text-muted">Total Songs</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meter Groups */}
      <div className="meter-groups">
        {meterData?.data.map((meterGroup) => (
          <div key={meterGroup.meter_id} className="meter-group mb-4">
            <div className="card">
              <div 
                className="card-header meter-header"
                onClick={() => toggleMeterExpansion(meterGroup.meter_id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      {meterGroup.meter}
                      {meterGroup.description && (
                        <span className="ms-2 text-muted small">({meterGroup.description})</span>
                      )}
                    </h5>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-primary me-3">
                      {meterGroup.song_count} {meterGroup.song_count === 1 ? 'song' : 'songs'}
                    </span>
                    <span className="expand-icon">
                      {expandedMeters.has(meterGroup.meter_id) ? '−' : '+'}
                    </span>
                  </div>
                </div>
              </div>
              
              {expandedMeters.has(meterGroup.meter_id) && (
                <div className="card-body">
                  <div className="song-list">
                    {meterGroup.songs.map((song, index) => (
                      <div key={song.song_id} className="song-item">
                        <div className="row align-items-center">
                          <div className="col-md-5">
                            <strong>{song.title}</strong>
                            {song.tune_name && (
                              <div className="text-muted small">Tune: {song.tune_name}</div>
                            )}
                          </div>
                          <div className="col-md-3">
                            {song.composer_name && (
                              <div className="text-muted small">
                                <Users size={14} className="me-1" />
                                Composer: {song.composer_name}
                              </div>
                            )}
                          </div>
                          <div className="col-md-3">
                            {song.lyricist_name && (
                              <div className="text-muted small">
                                <Users size={14} className="me-1" />
                                Lyricist: {song.lyricist_name}
                              </div>
                            )}
                          </div>
                          <div className="col-md-1 text-end">
                            <Link 
                              to={`/songs/${song.song_id}`} 
                              className="btn btn-sm btn-outline-primary"
                            >
                              Edit
                            </Link>
                          </div>
                        </div>
                        {index < meterGroup.songs.length - 1 && <hr className="my-2" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No data message */}
      {meterData?.data.length === 0 && (
        <div className="text-center text-muted py-5">
          <Music size={48} className="mb-3" />
          <p>No songs with meter information found.</p>
        </div>
      )}
      </div>
    </>
  );
};

export default MeterManagementPage;